import {
  Alert,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { customerCredentialsApi } from '../../api/customer-credentials.api.js';
import { useAuth } from '../../auth/useAuth.js';
import { APP_LOCALE } from '../../utils/locale.js';

/**
 * Changing what a customer signs in with, on their request.
 *
 * Tracker #31. A customer who has lost their mailbox, or wants to move to a
 * new one, rings support — and support had nothing to do it with. It is done
 * here, by staff, with a reason that goes on the customer's case file.
 *
 * The one thing this screen is careful about is the temporary password: it
 * appears exactly once, in the dialog that issued it, to be read out to the
 * customer. It is not shown again, not stored anywhere readable, and the
 * customer is made to replace it on their next sign-in — so nobody at Norvix
 * ends up knowing a customer's password.
 */
export function SignInDetailsCard({ customerId, currentEmail }: { customerId: string; currentEmail?: string }): JSX.Element {
  const [dialog, setDialog] = useState<'email' | 'password' | 'authenticator' | null>(null);
  const queryClient = useQueryClient();
  // Resetting a second factor is ADMIN only on the server (DEV-134); support sees the state, not the button.
  const isAdmin = useAuth().user?.role === 'admin';
  // DEV-134: Google Authenticator, and resetting it on the customer's request.
  const authenticator = useQuery({
    queryKey: ['customer-authenticator', customerId],
    queryFn: () => customerCredentialsApi.authenticator(customerId),
  });
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [issued, setIssued] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [notified, setNotified] = useState<boolean | undefined>(undefined);
  // DEV-134: every change emails the customer a security notice; say whether it went.
  const noticeLine = (sent: boolean | undefined): string => sent === true
    ? ' They were emailed a security notice.'
    : ' The security notice email could not be sent; tell the customer yourself.';

  const close = (): void => {
    setDialog(null);
    setEmail('');
    setReason('');
    setIssued(null);
    setCopied(false);
  };

  const changeEmail = useMutation({
    mutationFn: () => customerCredentialsApi.changeEmail(customerId, email.trim(), reason.trim()),
    onSuccess: (result) => {
      setDone(`Sign-in email changed to ${result.email}. Their sessions were ended; they sign in again with the new address.${noticeLine(result.customerNotified)}`);
      close();
    },
  });

  const resetPassword = useMutation({
    mutationFn: () => customerCredentialsApi.resetPassword(customerId, reason.trim()),
    onSuccess: (result) => { setIssued(result.temporaryPassword); setNotified(result.customerNotified); },
  });

  const resetAuthenticator = useMutation({
    mutationFn: () => customerCredentialsApi.resetAuthenticator(customerId, reason.trim()),
    onSuccess: async (result) => {
      setDone((result.removed
        ? 'Google Authenticator reset. Their sessions were ended; they set up a new one before their next payment or withdrawal.'
        : 'No Google Authenticator was active. Any setup in progress and any lockout were cleared, and their sessions were ended.'
      ) + noticeLine(result.customerNotified));
      close();
      await queryClient.invalidateQueries({ queryKey: ['customer-authenticator', customerId] });
    },
  });

  const authenticatorLine = authenticator.isLoading
    ? 'Google Authenticator: checking…'
    : authenticator.data === undefined
      ? 'Google Authenticator: could not be checked.'
      : authenticator.data.enrolled
        ? `Google Authenticator: set up${authenticator.data.enabledAt === null ? '' : ` on ${new Date(authenticator.data.enabledAt).toLocaleDateString(APP_LOCALE)}`}.`
        : 'Google Authenticator: not set up. They will be asked to set it up before their next payment or withdrawal.';

  const copyIssued = (): void => {
    if (issued === null) return;
    void navigator.clipboard.writeText(issued).then(() => setCopied(true));
  };

  const errorOf = (m: { error: unknown }): string | null =>
    m.error instanceof Error ? m.error.message : m.error === null ? null : 'The request did not go through.';

  return (
    <Card sx={{ mt: 2, mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Sign-in details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Changed here on the customer&apos;s request. Every action ends every session they have and
          is recorded on their case file with your reason.
          {currentEmail !== undefined && currentEmail !== '' ? ` Current email: ${currentEmail}.` : ''}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }} data-testid="authenticator-state">
          {authenticatorLine}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="outlined" onClick={() => setDialog('email')} sx={{ m: 0.5 }}>
            Change email
          </Button>
          <Button variant="outlined" onClick={() => setDialog('password')} sx={{ m: 0.5 }}>
            Issue temporary password
          </Button>
          {isAdmin ? (
            <Button variant="outlined" color="warning" onClick={() => setDialog('authenticator')} sx={{ m: 0.5 }}>
              Reset Google Authenticator
            </Button>
          ) : null}
        </Stack>
        {done !== null ? (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setDone(null)}>
            {done}
          </Alert>
        ) : null}
      </CardContent>

      <Dialog open={dialog === 'email'} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>Change sign-in email</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="New email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
            <TextField
              label="Reason"
              multiline
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              helperText="How you confirmed it was them, and why. Goes on the case file."
            />
            {errorOf(changeEmail) !== null ? <Alert severity="error">{errorOf(changeEmail)}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => changeEmail.mutate()}
            disabled={changeEmail.isPending || email.trim() === '' || reason.trim().length < 4}
          >
            {changeEmail.isPending ? 'Changing…' : 'Change email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === 'authenticator'} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Google Authenticator</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Only after you have confirmed it is really the customer: whoever sets up the next
              authenticator can authorise their payments and withdrawals. Their current authenticator
              stops working immediately, any setup in progress is cancelled, and every session ends.
              They set up a new one before their next payment or withdrawal, and should delete the
              old Norvix entry from their phone.
            </Alert>
            <TextField
              label="Reason"
              multiline
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
              helperText="How you confirmed it was them, and why (e.g. lost phone), at least 10 characters. Goes on the case file. Admins only."
            />
            {errorOf(resetAuthenticator) !== null ? <Alert severity="error">{errorOf(resetAuthenticator)}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => resetAuthenticator.mutate()}
            disabled={resetAuthenticator.isPending || reason.trim().length < 10}
          >
            {resetAuthenticator.isPending ? 'Resetting…' : 'Reset authenticator'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === 'password'} onClose={issued === null ? close : undefined} maxWidth="sm" fullWidth>
        <DialogTitle>{issued === null ? 'Issue a temporary password' : 'Temporary password issued'}</DialogTitle>
        <DialogContent>
          {issued === null ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                Their current password stops working immediately and every session ends. The new one
                is shown to you once, here, to read out to the customer. They must choose their own
                the next time they sign in.
              </Alert>
              <TextField
                label="Reason"
                multiline
                minRows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                autoFocus
                helperText="How you confirmed it was them, and why. Goes on the case file."
              />
              {errorOf(resetPassword) !== null ? <Alert severity="error">{errorOf(resetPassword)}</Alert> : null}
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">
                This is the only time it is shown. Read it to the customer now; when this dialog closes
                it is gone.
              </Alert>
              <Typography
                component="code"
                sx={{
                  fontFamily: 'var(--font-mono, monospace)',
                  fontSize: 22,
                  letterSpacing: '0.12em',
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  textAlign: 'center',
                  userSelect: 'all',
                }}
              >
                {issued}
              </Typography>
              <Button variant="outlined" onClick={copyIssued}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {issued === null ? (
            <>
              <Button onClick={close}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() => resetPassword.mutate()}
                disabled={resetPassword.isPending || reason.trim().length < 4}
              >
                {resetPassword.isPending ? 'Issuing…' : 'Issue password'}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => { setDone(`Temporary password issued. The customer must choose their own on next sign-in.${noticeLine(notified)}`); close(); }}>
              I have read it to the customer
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Card>
  );
}
